import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAnnotations } from '../contexts/AnnotationContext';
import { useBible } from '../contexts/BibleContext';
import Icon from '../components/UI/Icon';
import './BookmarksPage.css';

// Helper to parse OSIS reference (e.g., "Gen.1.1") into bookId and chapter
function parseVerseId(verseId) {
  if (!verseId) return { bookId: null, chapter: null, verse: null };
  const parts = verseId.split('.');
  return {
    bookId: parts[0] || null,
    chapter: parts[1] ? parseInt(parts[1], 10) : null,
    verse: parts[2] ? parseInt(parts[2], 10) : null
  };
}

// Helper to format a verse reference for display
function formatReference(verseId, books) {
  const { bookId, chapter, verse } = parseVerseId(verseId);
  if (!bookId) return verseId || 'Unknown';
  
  const book = books.find(b => b.id.toLowerCase() === bookId.toLowerCase());
  const bookName = book?.name || bookId;
  
  if (chapter && verse) {
    return `${bookName} ${chapter}:${verse}`;
  } else if (chapter) {
    return `${bookName} ${chapter}`;
  }
  return bookName;
}

// Helper to format earmark reference (chapter-level)
function formatEarmarkReference(item, books) {
  if (!item.chapterId) return 'Unknown';
  const parts = item.chapterId.split('.');
  const bookId = parts[0];
  const chapter = parts[1];
  
  const book = books.find(b => b.id.toLowerCase() === bookId.toLowerCase());
  const bookName = book?.name || bookId;
  
  return chapter ? `${bookName} ${chapter}` : bookName;
}

function BookmarksPage() {
  const navigate = useNavigate();
  const { bookmarks, highlights, notes, earmarks, removeBookmark, removeHighlight, deleteNote, removeEarmark } = useAnnotations();
  const { books } = useBible();
  const [activeTab, setActiveTab] = useState('bookmarks');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date');

  // Get current items based on active tab
  const currentItems = useMemo(() => {
    let items = [];
    switch (activeTab) {
      case 'bookmarks':
        items = bookmarks || [];
        break;
      case 'highlights':
        items = highlights || [];
        break;
      case 'notes':
        items = notes || [];
        break;
      case 'earmarks':
        items = (earmarks || []).map(e => ({
          ...e,
          verseId: e.chapterId // Map chapterId to verseId for compatibility
        }));
        break;
      default:
        items = bookmarks || [];
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      items = items.filter(item => {
        const ref = activeTab === 'earmarks' 
          ? `${item.bookName} ${item.chapterNum}`.toLowerCase()
          : formatReference(item.verseId, books).toLowerCase();
        return ref.includes(query) ||
          item.selectedText?.toLowerCase().includes(query) ||
          item.content?.toLowerCase().includes(query) ||
          item.label?.toLowerCase().includes(query) ||
          item.bookName?.toLowerCase().includes(query);
      });
    }

    // Sort
    if (sortBy === 'date') {
      items = [...items].sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
    } else if (sortBy === 'book') {
      items = [...items].sort((a, b) => {
        if (activeTab === 'earmarks') {
          if (a.bookName !== b.bookName) {
            return (a.bookName || '').localeCompare(b.bookName || '');
          }
          return (a.chapterNum || 0) - (b.chapterNum || 0);
        }
        const aRef = parseVerseId(a.verseId);
        const bRef = parseVerseId(b.verseId);
        if (aRef.bookId !== bRef.bookId) {
          return (aRef.bookId || '').localeCompare(bRef.bookId || '');
        }
        if (aRef.chapter !== bRef.chapter) {
          return (aRef.chapter || 0) - (bRef.chapter || 0);
        }
        return (aRef.verse || 0) - (bRef.verse || 0);
      });
    }

    return items;
  }, [activeTab, bookmarks, highlights, notes, earmarks, searchQuery, sortBy, books]);

  // Navigate to verse/chapter - parse the verseId to get bookId and chapter
  const handleGoToVerse = (item) => {
    // Handle earmarks (chapter-level, have chapterId format "BookId.Chapter")
    if (activeTab === 'earmarks' && item.chapterId) {
      const parts = item.chapterId.split('.');
      if (parts.length >= 2) {
        const bookId = parts[0];
        const chapter = parseInt(parts[1], 10);
        if (bookId && !isNaN(chapter) && chapter > 0) {
          navigate(`/reader/${bookId}/${chapter}`);
          return;
        }
      }
    }
    
    // Handle other items (verse-level)
    const { bookId, chapter } = parseVerseId(item.verseId);
    
    if (bookId && chapter) {
      const safeBookId = String(bookId).replace(/[^a-zA-Z0-9]/g, '');
      const safeChapter = parseInt(chapter, 10);
      
      if (safeBookId && !isNaN(safeChapter) && safeChapter > 0) {
        navigate(`/reader/${safeBookId}/${safeChapter}`);
        return;
      }
    }
    navigate('/reader');
  };

  // Handle delete
  const handleDelete = async (e, item) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to remove this item?')) return;
    
    try {
      if (activeTab === 'bookmarks') {
        await removeBookmark(item.id);
      } else if (activeTab === 'highlights' && removeHighlight) {
        await removeHighlight(item.id);
      } else if (activeTab === 'notes' && deleteNote) {
        await deleteNote(item.id);
      } else if (activeTab === 'earmarks' && removeEarmark) {
        await removeEarmark(item.id);
      }
    } catch (err) {
      console.error('Failed to delete item:', err);
    }
  };

  // Get highlight color
  const getHighlightColor = (color) => {
    const colors = {
      yellow: '#FEF3C7',
      green: '#D1FAE5',
      blue: '#DBEAFE',
      pink: '#FCE7F3',
      purple: '#EDE9FE'
    };
    return colors[color] || colors.yellow;
  };

  const getTabIcon = () => {
    switch (activeTab) {
      case 'bookmarks': return 'bookmark';
      case 'highlights': return 'highlight';
      case 'notes': return 'note';
      case 'earmarks': return 'earmark';
      default: return 'bookmark';
    }
  };

  return (
    <div className="bookmarks-page page">
      <header className="page-header">
        <h1 className="page-title">Saved</h1>
      </header>

      {/* Tab Navigation */}
      <div className="tabs-container">
        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'bookmarks' ? 'active' : ''}`}
            onClick={() => setActiveTab('bookmarks')}
          >
            <Icon name="bookmark" size={18} />
            <span>Bookmarks</span>
            {bookmarks.length > 0 && <span className="badge">{bookmarks.length}</span>}
          </button>
          <button 
            className={`tab ${activeTab === 'highlights' ? 'active' : ''}`}
            onClick={() => setActiveTab('highlights')}
          >
            <Icon name="highlight" size={18} />
            <span>Highlights</span>
            {highlights.length > 0 && <span className="badge">{highlights.length}</span>}
          </button>
          <button 
            className={`tab ${activeTab === 'notes' ? 'active' : ''}`}
            onClick={() => setActiveTab('notes')}
          >
            <Icon name="note" size={18} />
            <span>Notes</span>
            {notes.length > 0 && <span className="badge">{notes.length}</span>}
          </button>
          <button 
            className={`tab ${activeTab === 'earmarks' ? 'active' : ''}`}
            onClick={() => setActiveTab('earmarks')}
          >
            <Icon name="earmark" size={18} />
            <span>Earmarks</span>
            {earmarks.length > 0 && <span className="badge">{earmarks.length}</span>}
          </button>
        </div>
      </div>

      {/* Search and Sort */}
      <div className="toolbar">
        <div className="search-box">
          <Icon name="search" size={16} />
          <input
            type="text"
            placeholder={`Search ${activeTab}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="clear-btn" onClick={() => setSearchQuery('')}>
              <Icon name="x" size={14} />
            </button>
          )}
        </div>
        
        <select 
          className="sort-select"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          <option value="date">Recent First</option>
          <option value="book">By Book</option>
        </select>
      </div>

      {/* Content */}
      <div className="content">
        {currentItems.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <Icon name={getTabIcon()} size={48} />
            </div>
            <h3>No {activeTab} yet</h3>
            <p>
              {activeTab === 'bookmarks' && 'Bookmark verses while reading to save them here.'}
              {activeTab === 'highlights' && 'Highlight text in Scripture to collect it here.'}
              {activeTab === 'notes' && 'Add notes to verses while reading.'}
            </p>
            <button className="btn btn-primary" onClick={() => navigate('/reader')}>
              <Icon name="book" size={18} />
              Start Reading
            </button>
          </div>
        ) : (
          <div className="items-grid">
            {currentItems.map((item, index) => (
              <div 
                key={item.id || index} 
                className={`item-card ${activeTab}`}
                onClick={() => handleGoToVerse(item)}
              >
                {activeTab === 'highlights' && (
                  <div 
                    className="color-bar"
                    style={{ backgroundColor: getHighlightColor(item.color) }}
                  />
                )}
                
                <div className="card-content">
                  <div className="reference">
                    <Icon name={getTabIcon()} size={14} />
                    <span>
                      {activeTab === 'earmarks' 
                        ? formatEarmarkReference(item, books)
                        : formatReference(item.verseId, books)}
                    </span>
                  </div>
                  
                  {(activeTab === 'bookmarks' || activeTab === 'highlights') && item.selectedText && (
                    <p className="verse-text">"{item.selectedText}"</p>
                  )}
                  
                  {activeTab === 'notes' && (
                    <>
                      {item.selectedText && (
                        <p className="verse-text small">"{item.selectedText}"</p>
                      )}
                      <p className="note-content">{item.content}</p>
                    </>
                  )}
                  
                  {activeTab === 'earmarks' && (
                    <p className="earmark-label">
                      {item.bibleId ? `Bible: ${item.bibleId}` : 'Saved chapter'}
                    </p>
                  )}
                  
                  {item.label && <span className="label">{item.label}</span>}
                  
                  <div className="card-footer">
                    <span className="date">
                      {new Date(item.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                    <div className="actions">
                      <button 
                        className="action-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGoToVerse(item);
                        }}
                        title="Open in Reader"
                      >
                        <Icon name="book" size={16} />
                      </button>
                      <button 
                        className="action-btn delete"
                        onClick={(e) => handleDelete(e, item)}
                        title="Delete"
                      >
                        <Icon name="trash" size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default BookmarksPage;
